import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle, Phone, Mail, ArrowRight, Calendar, Users, BookOpen, Loader } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import useCampuses from "../hooks/useCampuses";
import useGrades from "../hooks/useGrades";
import SEO from "../components/SEO";

const CRM = process.env.REACT_APP_CRM_BACKEND;
const CRM_KEY = process.env.REACT_APP_CRM_API_KEY;

const Admissions = () => {
  const campusList = useCampuses();
  const campusNames = campusList.map((c) => c.name);
  const [form, setForm] = useState({ name: "", childName: "", email: "", phone: "", campus: "", campusId: "", grade: "", message: "" });
  const grades = useGrades(form.campusId);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.campus) return;
    setLoading(true);
    setError("");
    try {
      await axios.post(`${CRM}/api/public/leads`, {
        parent_name: form.name,
        child_name: form.childName || undefined,
        phone: form.phone,
        email: form.email || undefined,
        campus_preference: form.campus,
        grade_applying: form.grade || undefined,
        message: form.message || undefined,
        source: "Website",
        city: "Bengaluru",
        school: "TIPS Bengaluru",
      }, { headers: { "X-API-Key": CRM_KEY } });
      setSubmitted(true);
    } catch (err) {
      setError("Submission failed. Please call us at 7619305034.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main>
      <SEO
        title="Admissions 2026-27 | Apply Online"
        description="Apply for admissions at TIPS Bengaluru for 2026-27 academic year. CBSE school accepting applications for Nursery to Grade XII across 6 Bangalore campuses."
        keywords="TIPS Bengaluru admissions 2026, CBSE school admissions Bangalore, school admission form, TIPS school fees, school admission Bangalore 2026-27"
        canonical="/admissions"
      />
      {/* Page Header */}
      <section className="bg-[#1A0505] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-sm text-gray-400 mb-4">
            <Link to="/" className="hover:text-[#C83C4A] transition-colors">Home</Link>
            <span>/</span>
            <span className="text-white">Admissions</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white" style={{ fontFamily: "'Poppins', sans-serif" }}>Admissions</h1>
          <p className="text-gray-400 mt-3 max-w-xl">Join a community of 5000+ learners. Admissions are now open for 2026–27.</p>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-[#B41C2C] text-sm font-bold uppercase tracking-widest">Admission Process</span>
            <h2 className="mt-2 text-3xl font-bold text-gray-900" style={{ fontFamily: "'Poppins', sans-serif" }}>Simple Steps to Join TIPS</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { step: "01", title: "Submit Enquiry", desc: "Fill out the enquiry form below or call our admissions team at 7619305034.", icon: Phone },
              { step: "02", title: "Campus Visit", desc: "Schedule a tour of your nearest campus. Meet our educators and explore our facilities.", icon: Calendar },
              { step: "03", title: "Assessment", desc: "Students may be assessed based on age and grade. Parents attend an orientation session.", icon: BookOpen },
              { step: "04", title: "Enrolment", desc: "Complete the admission form, submit required documents, and pay the fees to confirm.", icon: Users }
            ].map(({ step, title, desc, icon: Icon }) => (
              <div key={step} className="relative">
                <div className="bg-[#FDF5F6] rounded-2xl p-6 border border-[#FEE7E9] h-full">
                  <div className="w-12 h-12 bg-[#9B1522] rounded-xl flex items-center justify-center mb-4">
                    <Icon size={22} className="text-white" />
                  </div>
                  <div className="text-4xl font-bold text-[#FEE7E9] mb-2" style={{ fontFamily: "'Poppins', sans-serif" }}>{step}</div>
                  <h4 className="font-bold text-gray-900 mb-2">{title}</h4>
                  <p className="text-sm text-gray-500 leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
            <div className="lg:col-span-2">
              <span className="text-[#B41C2C] text-sm font-bold uppercase tracking-widest">Apply Now</span>
              <h2 className="mt-2 text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: "'Poppins', sans-serif" }}>Begin Your Child's Journey</h2>
              <p className="text-gray-500 text-sm leading-relaxed mb-6">You and your child are welcome to join The Indian Public School community. We are committed to the holistic development of the children.</p>
              <div className="space-y-4">
                {[
                  { icon: CheckCircle, text: "CBSE Curriculum (Nursery to Grade XII)" },
                  { icon: CheckCircle, text: "Small class sizes for personal attention" },
                  { icon: CheckCircle, text: "State-of-the-art labs and facilities" },
                  { icon: CheckCircle, text: "Career counselling from Grade 9" },
                  { icon: CheckCircle, text: "Safe transport across Bengaluru" },
                  { icon: CheckCircle, text: "6 conveniently located campuses" }
                ].map(({ icon: Icon, text }) => (
                  <div key={text} className="flex items-center gap-2">
                    <Icon size={15} className="text-[#B41C2C] flex-shrink-0" />
                    <span className="text-sm text-gray-700">{text}</span>
                  </div>
                ))}
              </div>
              <div className="mt-8 p-4 bg-[#FDF5F6] rounded-xl border border-[#FEE7E9]">
                <p className="text-sm font-semibold text-gray-800 mb-3">Need Help?</p>
                <a href="tel:7619305034" className="flex items-center gap-2 text-[#9B1522] hover:text-[#87111E] font-semibold text-sm">
                  <Phone size={15} /> 7619305034
                </a>
                <a href="mailto:infobengaluru@tipsmail.net" className="flex items-center gap-2 text-[#9B1522] hover:text-[#87111E] font-semibold text-sm mt-2">
                  <Mail size={15} /> infobengaluru@tipsmail.net
                </a>
              </div>
            </div>

            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
                {!submitted ? (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <h3 className="text-xl font-bold text-gray-900" style={{ fontFamily: "'Poppins', sans-serif" }}>Admission Enquiry Form</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Parent / Guardian Name *</label>
                        <input required type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#B41C2C] focus:ring-1 focus:ring-[#B41C2C]" />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Child's Name *</label>
                        <input required type="text" value={form.childName} onChange={(e) => setForm({ ...form, childName: e.target.value })} placeholder="Child's full name" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#B41C2C] focus:ring-1 focus:ring-[#B41C2C]" />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Mobile Number *</label>
                        <input required type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="10-digit number" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#B41C2C] focus:ring-1 focus:ring-[#B41C2C]" />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Email Address</label>
                        <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="your@email.com" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#B41C2C] focus:ring-1 focus:ring-[#B41C2C]" />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Preferred Campus *</label>
                        <Select onValueChange={(v) => {
                            const c = campusList.find((x) => x.name === v);
                            setForm(prev => ({ ...prev, campus: v, campusId: c?.id || "", grade: "" }));
                          }}>
                          <SelectTrigger className="border-gray-200"><SelectValue placeholder="Select campus" /></SelectTrigger>
                          <SelectContent>{campusNames.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Grade Applying For</label>
                        <Select disabled={!form.campus} onValueChange={(v) => setForm(prev => ({ ...prev, grade: v }))}>
                          <SelectTrigger className="border-gray-200" disabled={!form.campus}>
                            <SelectValue placeholder={form.campus ? "Select grade" : "Select campus first"} />
                          </SelectTrigger>
                          <SelectContent>{grades.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Message / Questions</label>
                      <textarea rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Any specific questions or requirements?" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#B41C2C] focus:ring-1 focus:ring-[#B41C2C] resize-none" />
                    </div>
                    <button type="submit" disabled={loading} className="w-full bg-[#9B1522] hover:bg-[#87111E] text-white font-bold py-3 rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-70">
                      {loading ? <><Loader size={16} className="animate-spin" /> Submitting...</> : <>Submit Application <ArrowRight size={16} /></>}
                    </button>
                    {error && <p className="text-xs text-red-600 text-center">{error}</p>}
                    <p className="text-xs text-gray-400 text-center">Our team will contact you within 24 hours of submission.</p>
                  </form>
                ) : (
                  <div className="py-10 text-center">
                    <div className="w-16 h-16 bg-[#FEE7E9] rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle size={32} className="text-[#B41C2C]" />
                    </div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Poppins', sans-serif" }}>Application Received!</h4>
                    <p className="text-gray-500 mb-5">Thank you for your interest in TIPS Bengaluru. Our admissions team will contact you within 24 hours.</p>
                    <button onClick={() => { setSubmitted(false); setForm({ name: "", childName: "", email: "", phone: "", campus: "", campusId: "", grade: "", message: "" }); }} className="text-sm text-[#B41C2C] font-semibold hover:text-[#9B1522]">Submit another enquiry</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Documents Required */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "'Poppins', sans-serif" }}>Documents Required for Admission</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              "Birth Certificate of the child",
              "Aadhar Card of child and parents",
              "Previous school Transfer Certificate",
              "Last year's Progress Report / Report Card",
              "Passport size photographs (4 copies)",
              "Address Proof (Utility Bill / Rental Agreement)",
              "Blood Group Certificate",
              "Immunisation Record"
            ].map((doc) => (
              <div key={doc} className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3 border border-gray-100">
                <CheckCircle size={16} className="text-[#B41C2C] flex-shrink-0" />
                <span className="text-sm text-gray-700">{doc}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default Admissions;